import React from 'react'
import '../styles/components/structured-data-view.css'

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function formatKey(key) {
  return String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase())
}

function primitiveClass(value) {
  if (typeof value === 'boolean') return value ? 'is-true' : 'is-false'
  if (typeof value === 'number') return 'is-number'
  if (value == null || value === '') return 'is-empty'
  return 'is-text'
}

function renderPrimitive(value, emptyLabel) {
  if (value == null || value === '') return emptyLabel
  if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled'
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('en-US') : String(value)
  return String(value)
}

function StructuredValue({ value, depth, maxDepth, emptyLabel }) {
  if (depth > maxDepth) {
    return <span className="structured-data-primitive is-muted">Nested data</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="structured-data-primitive is-empty">{emptyLabel}</span>
    const primitiveArray = value.every((item) => !isPlainObject(item) && !Array.isArray(item))
    if (primitiveArray) {
      return (
        <div className="structured-data-chips">
          {value.map((item, index) => (
            <span key={`${item}-${index}`} className={`structured-data-chip ${primitiveClass(item)}`}>
              {renderPrimitive(item, emptyLabel)}
            </span>
          ))}
        </div>
      )
    }
    return (
      <ol className="structured-data-list">
        {value.map((item, index) => (
          <li key={index}>
            <StructuredValue value={item} depth={depth + 1} maxDepth={maxDepth} emptyLabel={emptyLabel} />
          </li>
        ))}
      </ol>
    )
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, item]) => item !== undefined)
    if (entries.length === 0) return <span className="structured-data-primitive is-empty">{emptyLabel}</span>
    return (
      <dl className={`structured-data-rows structured-data-depth-${Math.min(depth, 4)}`}>
        {entries.map(([key, item]) => (
          <div key={key} className="structured-data-row">
            <dt title={key}>{formatKey(key)}</dt>
            <dd>
              <StructuredValue value={item} depth={depth + 1} maxDepth={maxDepth} emptyLabel={emptyLabel} />
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  return (
    <span className={`structured-data-primitive ${primitiveClass(value)}`}>
      {renderPrimitive(value, emptyLabel)}
    </span>
  )
}

export default function StructuredDataView({
  value,
  title,
  className = '',
  compact = false,
  emptyLabel = '-',
  maxDepth = 5,
}) {
  return (
    <div className={`structured-data-view ${compact ? 'structured-data-view--compact' : ''} ${className}`.trim()}>
      {title ? <div className="structured-data-title">{title}</div> : null}
      <StructuredValue value={value} depth={0} maxDepth={maxDepth} emptyLabel={emptyLabel} />
    </div>
  )
}
